import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { createOrder } from "../lib/api";
import { formatUAH } from "../lib/format";
import { toast } from "sonner";

const FIELDS = [
  { name: "customer_name", label: "Ім'я та прізвище", required: true, placeholder: "Олена Коваленко" },
  { name: "phone", label: "Телефон", required: true, placeholder: "+380 67 000 00 00" },
  { name: "email", label: "Email", required: true, placeholder: "you@email.com", type: "email" },
  { name: "city", label: "Місто", required: true, placeholder: "Київ" },
  { name: "nova_poshta_branch", label: "Відділення Нової Пошти", required: true, placeholder: "Відділення №47" },
];

export default function Checkout() {
  const { items, total, clear } = useCart();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    for (const f of FIELDS) {
      if (f.required && !form[f.name]) {
        toast.error(`Заповніть поле: ${f.label}`);
        return;
      }
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email || "")) {
      toast.error("Введіть коректний email");
      return;
    }
    if (items.length === 0) {
      toast.error("Кошик порожній");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map(({ _key, image, ...rest }) => rest),
        total,
      };
      const order = await createOrder(payload);
      clear();
      navigate(`/thank-you?order=${order.order_number}`);
    } catch (err) {
      console.error(err);
      toast.error("Не вдалося оформити замовлення");
    } finally {
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-serif-display text-4xl text-silea-navy">Кошик порожній</h1>
        <p className="mt-3 text-silea-navy/60">Додайте моделі з колекції перед оформленням замовлення.</p>
        <Button asChild className="mt-8 bg-silea-navy hover:bg-silea-navy/90 text-white rounded-none">
          <Link to="/products">До колекції</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-silea-cream" data-testid="checkout-page">
      <div className="max-w-[1300px] mx-auto px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <p className="eyebrow text-silea-navy/60">Оформлення</p>
          <h1 className="font-serif-display text-5xl mt-3 text-silea-navy">Доставка</h1>
          <p className="mt-3 text-silea-navy/60 text-sm">
            Замовлення без реєстрації. Ми зв'яжемося з вами для підтвердження.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="checkout-form">
            {FIELDS.map((f) => (
              <div key={f.name}>
                <Label className="eyebrow text-silea-navy/70 mb-2 block">
                  {f.label}{f.required && " *"}
                </Label>
                <Input
                  type={f.type || "text"}
                  required={f.required}
                  placeholder={f.placeholder}
                  value={form[f.name] || ""}
                  onChange={(e) => onChange(f.name, e.target.value)}
                  data-testid={`checkout-${f.name}`}
                  className="rounded-none border-stone-300 h-12 focus-visible:ring-0 focus-visible:border-silea-navy bg-white"
                />
              </div>
            ))}
            <div>
              <Label className="eyebrow text-silea-navy/70 mb-2 block">Коментар (опціонально)</Label>
              <Textarea
                placeholder="Побажання до замовлення"
                value={form.note || ""}
                onChange={(e) => onChange("note", e.target.value)}
                data-testid="checkout-note"
                className="rounded-none border-stone-300 focus-visible:ring-0 focus-visible:border-silea-navy bg-white"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="place-order-btn"
              className="w-full bg-silea-navy hover:bg-silea-navy/90 text-white rounded-none h-14 eyebrow"
            >
              {submitting ? "Оформлюємо..." : `Підтвердити — ${formatUAH(total)}`}
            </Button>
            <p className="text-xs text-silea-navy/50 text-center">
              Натискаючи кнопку, ви погоджуєтеся з умовами покупки. Оплата за реквізитами після підтвердження.
            </p>
          </form>
        </div>

        <aside className="lg:col-span-5">
          <div className="bg-white border border-stone-200 p-6 sticky top-28">
            <h2 className="font-serif-display text-2xl text-silea-navy mb-5">Ваше замовлення</h2>
            <div className="space-y-5 max-h-[400px] overflow-auto pr-2">
              {items.map((it) => (
                <div key={it._key} className="flex gap-3" data-testid="checkout-line">
                  <div className="w-16 h-20 bg-stone-100 overflow-hidden flex-shrink-0">
                    <img src={it.image} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-display text-silea-navy text-lg leading-tight">{it.product_name}</p>
                    <p className="text-xs text-silea-navy/60">{it.color} · {it.size} · ×{it.quantity}</p>
                    {it.customizations.length > 0 && (
                      <p className="text-xs text-silea-navy/50 mt-1 line-clamp-2">
                        {it.customizations.map((c) => `+ ${c.label}${c.custom_text ? ` "${c.custom_text}"` : ""}`).join(" · ")}
                      </p>
                    )}
                  </div>
                  <p className="text-silea-navy font-medium text-sm whitespace-nowrap">{formatUAH(it.line_total)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-stone-200 mt-6 pt-5 space-y-2">
              <div className="flex justify-between text-sm text-silea-navy/70">
                <span>Підсумок</span><span>{formatUAH(total)}</span>
              </div>
              <div className="flex justify-between text-sm text-silea-navy/70">
                <span>Доставка</span><span>За тарифами Нової Пошти</span>
              </div>
              <div className="flex justify-between items-baseline pt-3 border-t border-stone-100 mt-3">
                <span className="eyebrow text-silea-navy/60">Разом</span>
                <span className="font-serif-display text-2xl text-silea-navy">{formatUAH(total)}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
